import { useSelector } from "react-redux";
import {
  IOrganizationUnit,
  IRoom,
  NendaProduct,
} from "../../../../../../../types/NendaTypes";
import { CustomerPortalState } from "../../../../store";

import { useParams } from "react-router-dom";
import { getCompanyIdByPremise } from "../../../../../../utils/company";
import { selectScreensByProduct } from "../../../../store/reducers/screenReducer";
import { CreatePromotionStateProps } from "./SelectRulesForm";
import ScreenSelect from "../../../ScreenSelect";
import { selectGroupsByNavigationLevel } from "@client/components/CustomerPortal/store/reducers/groupReducer";

const filterScreens = (
  premises: IOrganizationUnit[],
  screens: IRoom[],
  routeValues: { id: string; resource: string },
  bookingValues: {
    promotionId?: string;
    companyId?: string;
    premiseId?: string;
  }
): IRoom[] => {
  // We are editing an existing booking
  if (bookingValues.promotionId) {
    // The booking is owned by the premise
    if (bookingValues.premiseId) {
      return screens.filter((s) => s.hotel === bookingValues.premiseId);
    }
    // The booking is owned by the company
    else {
      // We are handling the company booking on the premise route
      // Only allow screens for that premise
      if (routeValues.resource === "premise") {
        return screens.filter((s) => s.hotel === routeValues.id);
      }
      // We still filter since user might be admin with access to multiple companies
      const companyPremiseIds = premises
        .filter((p) => getCompanyIdByPremise(p) === bookingValues.companyId)
        .map((p) => p._id);
      return screens.filter((s) => companyPremiseIds.includes(s.hotel));
    }
  }
  // We are creating a new booking
  else {
    // We are on the premise route
    if (routeValues.resource === "premise") {
      // Filter by premise
      return screens.filter((s) => s.hotel === routeValues.id);
    }
    // We are on the company route
    if (routeValues.resource === "company") {
      // Filter by company
      const companyPremiseIds = premises
        .filter((p) => getCompanyIdByPremise(p) === routeValues.id)
        .map((p) => p._id);
      return screens.filter((s) => companyPremiseIds.includes(s.hotel));
    }
    // Unreachable state, but TS cant process that all values of the union type are handeled
    // So a return is needed for TS compiliance
    return [];
  }
};

const AssignToScreensForm = ({
  state,
  setState,
}: CreatePromotionStateProps) => {
  const { id, type } = useParams<{ id: string; type: string }>();
  const { promotionId: editPromotionId, companyId, premiseId } = state;
  const premises = useSelector((state: CustomerPortalState) => {
    return state.organizationUnits.premises.data;
  });

  const groups = useSelector(selectGroupsByNavigationLevel);

  const allScreens = useSelector((state: CustomerPortalState) =>
    selectScreensByProduct(state, NendaProduct.SIGNAGE)
  ).filter((s) => s.signage.config?.arePromotionsEnabled); //Removes screens that have promotions disabled

  if (!type || !id) return null;

  const filteredPremises = premises.filter(
    (p) => p.signageConfig?.arePromotionsEnabled
  ); //Removes premises that have promotions disabled

  const filteredScreens = filterScreens(
    filteredPremises,
    allScreens,
    { id, resource: type },
    { promotionId: editPromotionId, companyId, premiseId }
  );

  const handleSelectScreen = (screens: IRoom[]) => {
    setState((prevState) => ({
      ...prevState,
      screens: screens.map((s) => s._id),
    }));
  };

  if (!filteredScreens) return null;

  const selectedScreens = filteredScreens.filter((s) =>
    state.screens.includes(s._id)
  );

  return (
    <ScreenSelect
      screens={filteredScreens}
      selectedScreens={selectedScreens}
      premises={filteredPremises}
      groups={groups}
      onScreenSelect={handleSelectScreen}
      mode="filter"
    />
  );
};

export default AssignToScreensForm;
